import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { flowRight, template } from "lodash";
import { v4 as uuid } from "uuid";

import {
    Button,
    Container,
    Content,
    Footer as RsuiteFooter,
    Header,
    Heading,
    HStack,
    Message,
    Nav,
    Schema,
    Text,
    toaster,
    Toggle,
} from "rsuite";
import { ToastContainerProps } from "rsuite/esm/toaster/ToastContainer";

import constants from "utils/constants";

import { AppDispatch, RootState } from "store/store";
import {
    getActiveEnvironmentId,
    getActiveEnvironmentMoniker,
} from "store/environments/environment-selectors";
import { getTenantId } from "store/environments/tenants/tenants-selectors";
import {
    setIsUnsavedChangeAvailable,
    setModalConfirmCallback,
} from "store/common/unsaved-data/unsaved-data-actions";
import { setRefreshActivated } from "store/common/page-refresh/page-refresh-actions";
import { fetchEnvironments } from "store/environments/environment-thunks";
import { fetchTenants } from "store/environments/tenants/tenants-thunks";
import { Features } from "store/environments/tenants/tenant-configurations/tenant-configurations-types";
import {
    ScheduleEventType,
    ScheduleIntervalType,
    SchedulerTime,
} from "store/environments/tenants/tenant-scheduler/tenant-scheduler-types";

import { getErrorMessage } from "api/defaults";
import {
    loadScheduleEvent,
    updateScheduleEvent,
} from "api/environments/tenants/schedule-events-api";
import { loadFeatureConfigurations } from "api/environments/tenants/tenant-feature-configurations-api";

import useRequestCancellation from "hooks/useRequestCancellation";
import useRefreshPage from "hooks/useRefreshPage";

import withLoader from "hocs/withLoader/withLoader";
import withError from "hocs/withError/withError";

import Footer from "components/Common/Footer/Footer";

import Table, { TableItem } from "./Table/Table";
import {
    createScheduleTime,
    defaultSchedulerTab,
    getTabLabelKey,
    isMachineLearningEnabled,
    isTabVisible,
    ScheduleEventForm,
    SchedulerTab,
    schedulerTabs,
    toScheduleEvent,
    toScheduleEventForm,
} from "./scheduler-helpers";

import "./Scheduler.less";

const useAppDispatch = () => useDispatch<AppDispatch>();

interface Props {
    showLoader?: (loaderVisibility: boolean) => void;
    showError?: (error: string) => void;
}

const Scheduler: React.FC<Props> = ({ showLoader, showError }) => {
    const { t } = useTranslation();
    const cancelTokenSource = useRequestCancellation();
    const params: Record<string, string> = useParams<{
        environmentId: string;
        tenantId: string;
    }>();

    const dispatch = useAppDispatch();

    const { environmentId, tenantId } = params;

    const activeEnvironmentId = useSelector((state: RootState): string =>
        getActiveEnvironmentId(state)
    );

    const envMoniker = useSelector((state: RootState): string =>
        getActiveEnvironmentMoniker(state)
    );

    const activeTenantId = useSelector((state: RootState): string =>
        getTenantId(state)
    );

    const isUnsavedChangeAvailable = useSelector(
        (state: RootState): boolean =>
            state.unsavedDataReducer.isUnsavedChangeAvailable
    );

    const [dataFetched, setDataFetched] = useState<boolean>(false);
    const [activeTab, setActiveTab] =
        useState<SchedulerTab>(defaultSchedulerTab);
    const [features, setFeatures] = useState<Features | null>(null);
    const [scheduler, setScheduler] = useState<ScheduleEventForm | null>(null);

    const toastOptions: ToastContainerProps = {
        duration: constants.alertDurationMsec,
    };

    const title = t("tenantConfig.scheduler.header");

    const visibleTabs = schedulerTabs.filter((tab) =>
        isTabVisible(tab, features)
    );

    const isRowLimitReached =
        (scheduler?.scheduleTimes.length ?? 0) >=
        constants.maxSchedulerRowsLimit;

    const fetchFeatures = async (): Promise<string | null> => {
        try {
            const [payload, status] = await loadFeatureConfigurations(
                envMoniker,
                activeTenantId,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setFeatures(payload);

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchScheduler = async (
        tab: SchedulerTab
    ): Promise<string | null> => {
        try {
            const [payload, status] = await loadScheduleEvent(
                activeEnvironmentId,
                activeTenantId,
                tab.type,
                tab.subtype,
                cancelTokenSource
            );
            if (status === constants.statusCode.OK && payload) {
                setScheduler(toScheduleEventForm(payload.data, tab));

                return null;
            }

            if (status === constants.statusCode.NoContent) {
                setScheduler(toScheduleEventForm(null, tab));

                return null;
            }

            return getErrorMessage(String(payload), status);
        } catch (e) {
            if (!axios.isCancel(e)) {
                return String(e);
            }
        }

        return null;
    };

    const fetchData = () => {
        showLoader?.(true);
        showError?.("");

        Promise.all([fetchFeatures(), fetchScheduler(activeTab)])
            .then((value) => {
                if (value.some((v) => typeof v == "string")) {
                    const errors = new Set<string>(
                        value.filter((v) => typeof v == "string")
                    );

                    const message = [...errors].join(". ");
                    showError?.(message);
                }
            })
            .finally(() => {
                showLoader?.(false);
                setDataFetched(true);
                dispatch(setIsUnsavedChangeAvailable(false));
            });
    };

    const selectTab = (tab: SchedulerTab) => {
        setActiveTab(tab);
        showLoader?.(true);

        fetchScheduler(tab)
            .then((error) => {
                if (error) {
                    showError?.(error);
                }
            })
            .finally(() => {
                showLoader?.(false);
            });
    };

    const handleTabSelect = (eventKey?: string) => {
        const tab = schedulerTabs.find((item) => item.key === eventKey);

        if (!tab || tab.key === activeTab.key) {
            return;
        }

        if (isUnsavedChangeAvailable) {
            dispatch(
                setModalConfirmCallback({
                    id: uuid(),
                    isAutoDeleteResistant: false,
                    isPageRedirect: true,
                    callback: () => {
                        dispatch(setIsUnsavedChangeAvailable(false));
                        selectTab(tab);
                    },
                })
            );
            dispatch(setRefreshActivated(true));

            return;
        }

        selectTab(tab);
    };

    const updateScheduler = (
        update: (scheduler: ScheduleEventForm) => ScheduleEventForm
    ) => {
        setScheduler((current) => (current ? update(current) : current));
        dispatch(setIsUnsavedChangeAvailable(true));
    };

    const handleToggleChange = (checked: boolean) => {
        updateScheduler((current) => ({
            ...current,
            enabled: checked,
            scheduleTimes:
                checked && current.scheduleTimes.length === 0
                    ? [createScheduleTime(activeTab)]
                    : current.scheduleTimes,
        }));
    };

    const handleChange = (data: TableItem<SchedulerTime>) => {
        updateScheduler((current) => ({
            ...current,
            scheduleTimes: current.scheduleTimes.map((item) =>
                item.key === data.key ? { ...data, ...validate(data) } : item
            ),
        }));
    };

    const handleAdd = () => {
        if (!scheduler?.enabled || isRowLimitReached) {
            return;
        }

        updateScheduler((current) => ({
            ...current,
            scheduleTimes: [
                ...current.scheduleTimes,
                createScheduleTime(activeTab),
            ],
        }));
    };

    const handleDelete = (data: TableItem<SchedulerTime>) => {
        if (!scheduler || scheduler.scheduleTimes.length <= 1) {
            return;
        }

        updateScheduler((current) => ({
            ...current,
            scheduleTimes: current.scheduleTimes.filter(
                (item) => item.key !== data.key
            ),
        }));
    };

    const handleSubmit = () => {
        if (!scheduler) {
            return;
        }

        const valid =
            !scheduler.enabled ||
            scheduler.scheduleTimes.every((item) => {
                const validated = validate(item);

                if (item.isValid !== validated.isValid) {
                    handleChange({ ...item, ...validated });
                }

                return validated.isValid;
            });

        if (!valid) {
            return;
        }

        if (
            scheduler.enabled &&
            activeTab.type ===
                ScheduleEventType.MachineLearningModelReTraining &&
            !isMachineLearningEnabled(features)
        ) {
            toaster.push(
                <Message showIcon type="error">
                    {t("tenantConfig.scheduler.mlSchedulerErrorMessage")}
                </Message>,
                toastOptions
            );

            return;
        }

        showLoader?.(true);

        updateScheduleEvent(
            activeEnvironmentId,
            activeTenantId,
            toScheduleEvent(scheduler)
        )
            .then(async ([payload, status]) => {
                if (status !== constants.statusCode.OK) {
                    toaster.push(
                        <Message showIcon type="error">
                            {getErrorMessage(String(payload), status)}
                        </Message>,
                        toastOptions
                    );

                    return;
                }

                toaster.push(
                    <Message showIcon type="success">
                        {template(t("tenantConfig.scheduler.successMessage"))({
                            eventType: t(getTabLabelKey(activeTab)),
                        })}
                    </Message>,
                    toastOptions
                );
                dispatch(setIsUnsavedChangeAvailable(false));

                const error = await fetchScheduler(activeTab);

                if (error) {
                    showError?.(error);
                }
            })
            .catch((e) => {
                if (!axios.isCancel(e)) {
                    toaster.push(
                        <Message showIcon type="error">
                            {String(e)}
                        </Message>,
                        toastOptions
                    );
                }
            })
            .finally(() => {
                showLoader?.(false);
            });
    };

    const validate = (data: TableItem<SchedulerTime>) => {
        const result = model.check({
            intervalInMinutes: data.intervalInMinutes,
            intervalType: data.intervalType,
            weekdays: data.weekdays,
        });

        return {
            isValid:
                !result?.intervalInMinutes?.hasError &&
                !result?.weekdays?.hasError,
            errors: {
                intervalInMinutes:
                    result?.intervalInMinutes?.errorMessage ?? "",
                weekdays: result?.weekdays?.errorMessage ?? "",
            },
        };
    };

    useEffect(() => {
        if (envMoniker && activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    }, [envMoniker, activeEnvironmentId, activeTenantId]);

    useRefreshPage(() => {
        if (envMoniker && activeEnvironmentId && activeTenantId) {
            fetchData();
        }
    });

    useEffect(() => {
        if (!activeEnvironmentId) {
            dispatch(fetchEnvironments(environmentId));
        }
    }, [activeEnvironmentId, dispatch]);

    useEffect(() => {
        if (!activeTenantId && envMoniker) {
            dispatch(fetchTenants(envMoniker, tenantId));
        }
    }, [envMoniker, dispatch]);

    useEffect(() => {
        if (features && !isTabVisible(activeTab, features)) {
            selectTab(defaultSchedulerTab);
        }
    }, [features]);

    const { NumberType } = Schema.Types;

    const isMissing = (value: string | number | null | undefined) =>
        value === null || value === undefined || value === "";

    const model = Schema.Model({
        intervalInMinutes: NumberType()
            // A priority rule, so it also runs when the value is empty.
            .addRule(
                (value, data) =>
                    data.intervalType !== ScheduleIntervalType.Minutes ||
                    !isMissing(value),
                t("tenantConfig.scheduler.required"),
                true
            )
            .addRule(
                (value, data) =>
                    data.intervalType !== ScheduleIntervalType.Minutes ||
                    isMissing(value) ||
                    (Number(value) >= constants.minSchedulerIntervalLimit &&
                        Number(value) <=
                            constants.maxSchedulerIntervalValueLimit),
                t("tenantConfig.scheduler.intervalInRange")
            ),
        intervalType: NumberType(),
        weekdays: NumberType()
            .isRequired(t("tenantConfig.scheduler.required"))
            .addRule(
                (value) => Number(value ?? 0) > 0,
                t("tenantConfig.scheduler.required")
            ),
    });

    return (
        dataFetched &&
        scheduler && (
            <Container className="scheduler__container">
                <Header>
                    <Heading level={1}>{title}</Heading>
                </Header>

                <Content className="scheduler__content">
                    <Nav
                        appearance="subtle"
                        activeKey={activeTab.key}
                        onSelect={handleTabSelect}
                        className="navbar__container scheduler__tab-bar"
                    >
                        {visibleTabs.map((tab) => (
                            <Nav.Item key={tab.key} eventKey={tab.key}>
                                {t(getTabLabelKey(tab))}
                            </Nav.Item>
                        ))}
                    </Nav>

                    <HStack
                        className="scheduler__toolbar"
                        justifyContent="space-between"
                    >
                        <HStack spacing={10}>
                            <Toggle
                                checked={scheduler.enabled}
                                className="scheduler__toggle"
                                checkedChildren={t("toggler.onTitle")}
                                unCheckedChildren={t("toggler.offTitle")}
                                onChange={handleToggleChange}
                            />
                            <Text>
                                {template(
                                    t("tenantConfig.scheduler.toggleControl")
                                )({
                                    eventType: t(getTabLabelKey(activeTab)),
                                })}
                            </Text>
                        </HStack>

                        <Button
                            appearance="ghost"
                            className="scheduler__add-row-btn"
                            disabled={!scheduler.enabled || isRowLimitReached}
                            onClick={handleAdd}
                        >
                            {t("tenantConfig.addRowButton")}
                        </Button>
                    </HStack>

                    <Table
                        items={scheduler.scheduleTimes}
                        isExtended={activeTab.isExtended}
                        disabled={!scheduler.enabled}
                        emptyText={t("tenantConfig.scheduler.turnedOff")}
                        onRowChange={handleChange}
                        onRowDelete={handleDelete}
                    />
                </Content>

                <RsuiteFooter>
                    <Footer onSubmit={handleSubmit} />
                </RsuiteFooter>
            </Container>
        )
    );
};

const Component = flowRight(withLoader, withError)(Scheduler);

export default Component;
